// OPERATOR-CONSOLE: each provisioning key's label, (i) text, how its value is shown on the card
// and the form its Edit modal holds. Shapes: packages/schema contracts/api/provisioning.ts.
import type {
  BaselineSetting,
  BasePromptSetting,
  ContainerMemorySetting,
  LimitsSetting,
  ModelsSetting,
  NightlyReviewSetting,
  RolloutSetting,
  RuntimeProfilesSetting,
  SkillsDefaultsSetting,
} from "@companyos/schema/contracts";
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import type { ReactNode } from "react";
import { DENSE_TABLE } from "../../settings/row-actions";
import {
  CheckField,
  KvList,
  leafText,
  MoneyField,
  NumberField,
  type Obj,
  ObjectForm,
  ObjectView,
  RowsEditor,
  SelectField,
  TextField,
} from "./fields";

export type Named = { id: string; name: string };
export type KindCtx = {
  companies: readonly Named[];
  satellites: readonly Named[];
  scope: "platform" | "company" | "satellite";
};
type ViewProps = { value: unknown; ctx: KindCtx };
type FormProps = { draft: Obj; set: (next: Obj) => void; ctx: KindCtx };

export type Kind = {
  label: string;
  info: ReactNode;
  View: (p: ViewProps) => ReactNode;
  Form: (p: FormProps) => ReactNode;
  /** The modal's width. */
  wide?: boolean;
};

export const EDITED_KEYS = [
  "models",
  "runtime_profiles",
  "container_memory",
  "baseline",
  "bridge_runtime",
  "satellite_runtime",
  "base_prompt",
  "skills_defaults",
  "rollout",
  "limits",
  "de_defaults",
  "nightly_review",
] as const;
export type EditedKey = (typeof EDITED_KEYS)[number];

const EFFORTS = ["off", "low", "medium", "high"] as const;
const PROFILES = ["light", "standard", "heavy"] as const;
const PROFILE_FIELDS = [
  ["max_sessions", "Sessions"],
  ["max_runs", "Runs"],
  ["kanban_max_in_progress", "Helper cards"],
  ["dispatch_interval_seconds", "Dispatch (s)"],
] as const;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const nameOf = (list: readonly Named[], id: string | null) =>
  id ? (list.find((x) => x.id === id)?.name ?? `${id.slice(0, 8)}…`) : null;

function Grid({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <Table className={DENSE_TABLE}>
      <TableHeader>
        <TableRow>
          {head.map((h, i) => (
            <TableHead key={h} scope="col" className={i > 0 ? "text-right" : undefined}>
              {h}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: display rows
          <TableRow key={i}>
            {r.map((c, j) =>
              j === 0 ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: display cells
                <TableHead key={j} scope="row" className="font-normal">
                  {c}
                </TableHead>
              ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: display cells
                <TableCell key={j} className="text-right tabular-nums">
                  {c}
                </TableCell>
              ),
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const Muted = ({ children }: { children: ReactNode }) => (
  <p className="text-xs text-muted-foreground">{children}</p>
);

// Models -------------------------------------------------------------------------------------------
const models: Kind = {
  label: "Models",
  info: "The model, effort and vision per role kind, and the vision helper model. Empty: the effort defaults decide.",
  wide: true,
  View: ({ value }) => {
    const v = value as ModelsSetting;
    return (
      <div className="grid gap-2">
        {v.kinds.length === 0 ? (
          <Muted>No role kinds: the effort defaults decide.</Muted>
        ) : (
          <Table className={DENSE_TABLE} data-slot="models-kinds">
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Role kind</TableHead>
                <TableHead scope="col">Model</TableHead>
                <TableHead scope="col">Effort</TableHead>
                <TableHead scope="col">Vision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {v.kinds.map((k) => (
                <TableRow key={k.kind} data-kind={k.kind}>
                  <TableHead scope="row" className="font-mono text-xs font-normal">
                    {k.kind}
                  </TableHead>
                  <TableCell className="break-all">{k.model}</TableCell>
                  <TableCell>{cap(k.effort)}</TableCell>
                  <TableCell>{k.vision ? "Yes" : "No"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <KvList
          rows={[{ key: "aux", label: "Vision helper model", value: v.aux_vision_model ?? "None" }]}
        />
      </div>
    );
  },
  Form: ({ draft, set }) => {
    const v = draft as unknown as ModelsSetting;
    return (
      <>
        <RowsEditor
          label="Role kinds"
          addLabel="Add kind"
          slot="models-rows"
          rows={v.kinds}
          blank={() => ({ kind: "", model: "", effort: "medium", vision: false })}
          columns={[
            { key: "kind", label: "Kind", kind: "text", placeholder: "sdr" },
            { key: "model", label: "Model", kind: "text", placeholder: "deepseek-flash" },
            {
              key: "effort",
              label: "Effort",
              kind: "select",
              options: EFFORTS.map((e) => ({ value: e, label: cap(e) })),
            },
            { key: "vision", label: "Vision", kind: "check" },
          ]}
          onChange={(kinds) => set({ ...draft, kinds })}
        />
        <TextField
          label="Vision helper model"
          nullable
          placeholder="None"
          value={v.aux_vision_model}
          onChange={(x) => set({ ...draft, aux_vision_model: x })}
        />
      </>
    );
  },
};

// Runtime profiles -------------------------------------------------------------------------------
const runtimeProfiles: Kind = {
  label: "Runtime profiles",
  info: "Concurrency and dispatch per container profile, as the ClawPanel baseline has them. A satellite override replaces all three. The children cap is the company's; memory is under Container memory.",
  wide: true,
  View: ({ value }) => {
    const v = value as RuntimeProfilesSetting;
    return (
      <div data-slot="runtime-grid">
        <Grid
          head={["", ...PROFILES.map(cap)]}
          rows={PROFILE_FIELDS.map(([f, l]) => [
            l,
            ...PROFILES.map((p) => v[p][f].toLocaleString("en-US")),
          ])}
        />
      </div>
    );
  },
  Form: ({ draft, set }) => {
    const v = draft as unknown as RuntimeProfilesSetting;
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {PROFILES.map((p) => (
          <fieldset key={p} className="grid gap-2" data-profile={p}>
            <legend className="mb-1 text-sm font-medium">{cap(p)}</legend>
            {PROFILE_FIELDS.map(([f, l]) => (
              <NumberField
                key={f}
                label={`${cap(p)} ${l.toLowerCase()}`}
                value={v[p][f]}
                onChange={(n) => set({ ...draft, [p]: { ...v[p], [f]: n } })}
              />
            ))}
          </fieldset>
        ))}
      </div>
    );
  },
};

// Container memory -------------------------------------------------------------------------------
const containerMemory: Kind = {
  label: "Container memory",
  info: "The container memory limit (MB) per runtime profile: the provisioning default for a container of that profile.",
  View: ({ value }) => {
    const v = value as ContainerMemorySetting;
    return (
      <div data-slot="container-memory-grid">
        <Grid
          head={["", ...PROFILES.map(cap)]}
          rows={[["Memory (MB)", ...PROFILES.map((p) => v[p].toLocaleString("en-US"))]]}
        />
      </div>
    );
  },
  Form: ({ draft, set }) => {
    const v = draft as unknown as ContainerMemorySetting;
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {PROFILES.map((p) => (
          <NumberField
            key={p}
            label={`${cap(p)} memory (MB)`}
            value={v[p]}
            onChange={(n) => set({ ...draft, [p]: n })}
          />
        ))}
      </div>
    );
  },
};

// Baseline -----------------------------------------------------------------------------------------
const PROFILE_OPTIONS = PROFILES.map((p) => ({ value: p, label: cap(p) }));
const baseline: Kind = {
  label: "Baseline",
  info: "The ClawPanel baseline version and container profile. Per container at satellite scope.",
  wide: true,
  View: ({ value }) => {
    const v = value as BaselineSetting;
    return (
      <div className="grid gap-2">
        <KvList
          rows={[
            { key: "version", label: "Version", value: v.version ?? "Shipped with ClawPanel" },
            { key: "profile", label: "Profile", value: cap(v.profile) },
          ]}
        />
        {v.containers.length > 0 ? (
          <Grid
            head={["Container", "Version", "Profile"]}
            rows={v.containers.map((c) => [
              c.container,
              c.version ?? "Same",
              c.profile ? cap(c.profile) : "Same",
            ])}
          />
        ) : null}
      </div>
    );
  },
  Form: ({ draft, set, ctx }) => {
    const v = draft as unknown as BaselineSetting;
    return (
      <>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Version"
            nullable
            placeholder="Shipped with ClawPanel"
            value={v.version}
            onChange={(x) => set({ ...draft, version: x })}
          />
          <SelectField
            label="Profile"
            value={v.profile}
            options={PROFILE_OPTIONS}
            onChange={(x) => set({ ...draft, profile: x })}
          />
        </div>
        {ctx.scope === "satellite" ? (
          <RowsEditor
            label="Containers"
            addLabel="Add container"
            slot="baseline-containers"
            rows={v.containers}
            blank={() => ({ container: "", version: null, profile: null })}
            columns={[
              { key: "container", label: "Container", kind: "text", placeholder: "acme-hermes" },
              { key: "version", label: "Version", kind: "nullable", placeholder: "Same" },
              {
                key: "profile",
                label: "Profile",
                kind: "select",
                options: [{ value: "", label: "Same" }, ...PROFILE_OPTIONS],
              },
            ]}
            onChange={(containers) => set({ ...draft, containers })}
          />
        ) : null}
      </>
    );
  },
};

// Wake ---------------------------------------------------------------------------------------------
const bridgeRuntime: Kind = {
  label: "Wake bridge",
  info: "How the wake bridge runs a self-hosted DE: polling, leases, run timeout, helper-card wakes, file folders.",
  wide: true,
  View: ({ value }) => <ObjectView value={value} />,
  Form: ({ draft, set }) => (
    <ObjectForm
      value={draft}
      onChange={set}
      enums={{ run_binding: ["key", "args", "header"], run_via: ["gateway", "chat"] }}
    />
  ),
};

const satelliteRuntime: Kind = {
  label: "Satellite runtime",
  info: "The wake listener's port and how often a satellite refreshes its runtime. Releases are pinned on the Satellites page.",
  View: ({ value }) => {
    const v = value as {
      releases: { bridge: { version: string } | null; render_service: { version: string } | null };
    };
    return (
      <div className="grid gap-1">
        <ObjectView value={value} skip={["releases"]} />
        <KvList
          rows={[
            {
              key: "bridge",
              label: "Bridge release",
              value: v.releases.bridge?.version ?? "Not pinned",
            },
            {
              key: "render",
              label: "Render service release",
              value: v.releases.render_service?.version ?? "Not pinned",
            },
          ]}
        />
      </div>
    );
  },
  Form: ({ draft, set }) => <ObjectForm value={draft} onChange={set} skip={["releases"]} />,
};

// Base prompt ----------------------------------------------------------------------------------------
const basePrompt: Kind = {
  label: "Base prompt version",
  info: "Which base prompt version is served with the wake. Empty: the version the code ships.",
  View: ({ value }) => (
    <KvList
      rows={[
        {
          key: "version",
          label: "Pinned version",
          value: (value as BasePromptSetting).version ?? "Shipped with the code",
        },
      ]}
    />
  ),
  Form: ({ draft, set }) => (
    <TextField
      label="Pinned version"
      nullable
      placeholder="Shipped with the code"
      value={(draft as unknown as BasePromptSetting).version}
      onChange={(x) => set({ ...draft, version: x })}
    />
  ),
};

// Skills library ---------------------------------------------------------------------------------------
const skillsDefaults: Kind = {
  label: "Library packs",
  info: "The skill packs every company starts with, their pinned versions and the feature each switches on. Empty: the platform's own packs.",
  wide: true,
  View: ({ value }) => {
    const v = value as SkillsDefaultsSetting;
    return v.packs.length === 0 ? (
      <Muted>No packs listed: the platform's own packs apply.</Muted>
    ) : (
      <Grid
        head={["Pack", "Version", "Core", "Gated feature"]}
        rows={v.packs.map((p) => [
          <code key="k">{p.key}</code>,
          p.version ?? "Newest",
          p.core ? "Yes" : "No",
          p.gated_feature ?? "—",
        ])}
      />
    );
  },
  Form: ({ draft, set }) => (
    <RowsEditor
      label="Packs"
      addLabel="Add pack"
      slot="packs-rows"
      rows={(draft as unknown as SkillsDefaultsSetting).packs}
      blank={() => ({ key: "", version: null, core: false, gated_feature: null })}
      columns={[
        { key: "key", label: "Pack", kind: "text", placeholder: "render-visuals" },
        { key: "version", label: "Version", kind: "nullable", placeholder: "Newest" },
        { key: "core", label: "Core", kind: "check" },
        { key: "gated_feature", label: "Gated feature", kind: "nullable", placeholder: "None" },
      ]}
      onChange={(packs) => set({ ...draft, packs })}
    />
  ),
};

// Rollout ------------------------------------------------------------------------------------------------
const rollout: Kind = {
  label: "Rollout",
  info: "Where a change lands first (the canary; the home company unless another is named), then a volunteer, the watch window and the auto-stop.",
  wide: true,
  View: ({ value, ctx }) => {
    const v = value as RolloutSetting;
    return (
      <KvList
        rows={[
          {
            key: "canary",
            label: "Canary",
            value: nameOf(ctx.companies, v.canary_company_id) ?? "Home company",
          },
          {
            key: "volunteer",
            label: "Volunteer",
            value: nameOf(ctx.companies, v.volunteer_company_id) ?? "None",
          },
          { key: "window", label: "Window", value: `${v.window_hours} h or ${v.window_runs} runs` },
          { key: "stop", label: "Auto-stop factor", value: `× ${v.auto_stop_factor}` },
          {
            key: "waves",
            label: "Wave order",
            value:
              v.wave_order.length === 0
                ? "The rest, any order"
                : v.wave_order.map((id) => nameOf(ctx.companies, id)).join(" → "),
          },
        ]}
      />
    );
  },
  Form: ({ draft, set, ctx }) => {
    const v = draft as unknown as RolloutSetting;
    const opts = (none: string) => [
      { value: "", label: none },
      ...ctx.companies.map((c) => ({ value: c.id, label: c.name })),
    ];
    return (
      <>
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField
            label="Canary"
            value={v.canary_company_id ?? ""}
            options={opts("Home company")}
            onChange={(x) => set({ ...draft, canary_company_id: x || null })}
          />
          <SelectField
            label="Volunteer"
            value={v.volunteer_company_id ?? ""}
            options={opts("None")}
            onChange={(x) => set({ ...draft, volunteer_company_id: x || null })}
          />
          <NumberField
            label="Window hours"
            value={v.window_hours}
            min={1}
            max={720}
            onChange={(n) => set({ ...draft, window_hours: n })}
          />
          <NumberField
            label="Window runs"
            value={v.window_runs}
            min={1}
            onChange={(n) => set({ ...draft, window_runs: n })}
          />
          <NumberField
            label="Auto-stop factor"
            value={v.auto_stop_factor}
            min={1}
            max={10}
            step={0.1}
            onChange={(n) => set({ ...draft, auto_stop_factor: n })}
          />
        </div>
        <RowsEditor
          label="Wave order"
          addLabel="Add company"
          slot="wave-rows"
          rows={v.wave_order.map((id) => ({ id }))}
          blank={() => ({ id: ctx.companies[0]?.id ?? "" })}
          columns={[
            {
              key: "id",
              label: "Company",
              kind: "select",
              options: ctx.companies.map((c) => ({ value: c.id, label: c.name })),
            },
          ]}
          onChange={(rows) => set({ ...draft, wave_order: rows.map((r) => r.id) })}
        />
      </>
    );
  },
};

// Limits ---------------------------------------------------------------------------------------------------
const limits: Kind = {
  label: "Limits",
  info: "The company envelope across its DEs, the system and operator agents' daily caps, and money limits for outward actions.",
  wide: true,
  View: ({ value }) => {
    const v = value as LimitsSetting;
    return (
      <KvList
        rows={[
          {
            key: "envelope",
            label: "Company envelope / month",
            value: leafText("x_minor", v.company_envelope_minor),
          },
          { key: "hard", label: "Stop at the envelope", value: v.hard_cap ? "Yes" : "Alert only" },
          {
            key: "system",
            label: "System agent / day",
            value: leafText("x_micros", v.system_agent_daily_cap_micros),
          },
          {
            key: "operator",
            label: "Operator agent / day",
            value: leafText("x_micros", v.operator_agent_daily_cap_micros),
          },
          {
            key: "tx",
            label: "Per transaction",
            value: leafText("x_minor", v.money.per_transaction_minor),
          },
          { key: "day", label: "Per day", value: leafText("x_minor", v.money.per_day_minor) },
        ]}
      />
    );
  },
  Form: ({ draft, set }) => (
    <ObjectForm
      value={draft}
      onChange={set}
      money={[
        "company_envelope_minor",
        "system_agent_daily_cap_micros",
        "operator_agent_daily_cap_micros",
        "money.per_transaction_minor",
        "money.per_day_minor",
      ]}
    />
  ),
};

// DE budget (de_defaults' budget part) -----------------------------------------------------------------
type Budget = {
  amount_minor: number;
  hard_cap: boolean;
  soft_thresholds: string[];
  alert_only: boolean;
} | null;
const deDefaults: Kind = {
  label: "DE budget",
  info: "The monthly budget every new DE gets (in the company's currency). The rest of the DE set-up keeps its values.",
  View: ({ value }) => {
    const b = (value as { budget: Budget }).budget;
    return b === null ? (
      <Muted>No budget for new DEs.</Muted>
    ) : (
      <KvList
        rows={[
          { key: "amount", label: "Per month", value: leafText("x_minor", b.amount_minor) },
          { key: "hard", label: "Hard cap", value: b.hard_cap ? "Yes" : "No" },
          {
            key: "alerts",
            label: "Alerts at",
            value:
              b.soft_thresholds.length === 0
                ? "None"
                : b.soft_thresholds.map((t) => `${Math.round(Number(t) * 100)}%`).join(", "),
          },
          { key: "alert", label: "Alert only", value: b.alert_only ? "Yes" : "No" },
        ]}
      />
    );
  },
  Form: ({ draft, set }) => {
    const b = draft.budget as Budget;
    const put = (next: Budget) => set({ ...draft, budget: next });
    return (
      <>
        <CheckField
          label="Give new DEs a budget"
          checked={b !== null}
          onChange={(on) =>
            put(
              on
                ? {
                    amount_minor: 500,
                    hard_cap: true,
                    soft_thresholds: ["0.5", "0.8"],
                    alert_only: false,
                  }
                : null,
            )
          }
        />
        {b ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <MoneyField
              label="Per month"
              minor
              value={b.amount_minor}
              onChange={(x) => put({ ...b, amount_minor: x ?? 0 })}
            />
            <TextField
              label="Alerts at (shares, comma separated)"
              value={b.soft_thresholds.join(", ")}
              onChange={(x) =>
                put({
                  ...b,
                  soft_thresholds: (x ?? "")
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
            <CheckField
              label="Hard cap"
              checked={b.hard_cap}
              onChange={(x) => put({ ...b, hard_cap: x })}
            />
            <CheckField
              label="Alert only"
              checked={b.alert_only}
              onChange={(x) => put({ ...b, alert_only: x })}
            />
          </div>
        ) : null}
      </>
    );
  },
};

/**
 * NIGHTLY-REVIEW: when each digital employee's nightly self-review runs (a window in company
 * time, every DE at its own stable minute inside it), how many run at once across the platform,
 * the cost cap per company per night, the proposals cap per DE and the model.
 */
const nightlyReview: Kind = {
  label: "Nightly review",
  info: "Each digital employee reviews its own day every night and proposes improvements (never changes anything). The window is company time; every DE gets a stable minute inside it, so the fleet never piles up. The concurrency is platform-wide. The flag NIGHTLY_REVIEW=1 on the worker turns it on.",
  View: ({ value }) => {
    const v = value as NightlyReviewSetting;
    return (
      <KvList
        rows={[
          {
            key: "window",
            label: "Window (company time)",
            value: `${v.window_start}–${v.window_end}`,
          },
          { key: "conc", label: "At once, platform-wide", value: String(v.concurrency) },
          {
            key: "cap",
            label: "Cost cap / company / night",
            value: leafText("x_micros", v.cost_cap_micros),
          },
          { key: "max", label: "Proposals / DE / night", value: String(v.max_proposals) },
          { key: "model", label: "Model", value: v.model },
        ]}
      />
    );
  },
  Form: ({ draft, set }) => <ObjectForm value={draft} onChange={set} money={["cost_cap_micros"]} />,
};

export const KINDS: Record<EditedKey, Kind> = {
  models,
  runtime_profiles: runtimeProfiles,
  container_memory: containerMemory,
  baseline,
  bridge_runtime: bridgeRuntime,
  satellite_runtime: satelliteRuntime,
  base_prompt: basePrompt,
  skills_defaults: skillsDefaults,
  rollout,
  limits,
  de_defaults: deDefaults,
  nightly_review: nightlyReview,
};

/** A small mark for a key's override source. */
export function SourceBadge({
  source,
}: {
  source: "satellite" | "company" | "platform" | "default";
}) {
  const map = {
    satellite: { label: "Satellite override", variant: "warning" },
    company: { label: "Company override", variant: "info" },
    platform: { label: "Platform", variant: "accent" },
    default: { label: "Default", variant: "outline" },
  } as const;
  const m = map[source];
  return (
    <Badge variant={m.variant} data-source={source}>
      {m.label}
    </Badge>
  );
}
